import React, { useState, useEffect } from 'react';
import { getTickets, createTicket, getTicketMessages, addTicketMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', priority: 'MEDIUM' });
  const [reply, setReply] = useState('');

  const fetchTickets = async () => {
    try {
      const response = await getTickets();
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createTicket(newTicket.subject, newTicket.message, newTicket.priority);
      toast.success('Ticket created successfully');
      setShowCreate(false);
      setNewTicket({ subject: '', message: '', priority: 'MEDIUM' });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const openTicket = async (ticket) => {
    setSelected(ticket);
    try {
      const response = await getTicketMessages(ticket.id);
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await addTicketMessage(selected.id, reply);
      setReply('');
      const response = await getTicketMessages(selected.id);
      setMessages(response.data);
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <div className="p-8" data-testid="client-support-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Support</h1>
          <p className="text-slate-600 mt-1">{tickets.length} tickets</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-ticket-btn"><Plus size={18} className="mr-2" /> New Ticket</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Support Ticket</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Subject</Label>
                <Input required value={newTicket.subject} onChange={e => setNewTicket({...newTicket, subject: e.target.value})} data-testid="ticket-subject-input" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={newTicket.priority} onValueChange={v => setNewTicket({...newTicket, priority: v})}>
                  <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Message</Label>
                <Textarea required rows={5} value={newTicket.message} onChange={e => setNewTicket({...newTicket, message: e.target.value})} data-testid="ticket-message-input" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="ticket-submit-btn">Create Ticket</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <table className="w-full text-sm" data-testid="client-tickets-table">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Subject</th>
              <th className="p-4">Priority</th>
              <th className="p-4">Status</th>
              <th className="p-4">Updated</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-slate-500">No tickets yet</td></tr>
              : tickets.map(ticket => (
                <tr key={ticket.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-4 font-medium">{ticket.subject}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      ticket.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                      ticket.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-slate-100 text-slate-600'
                    }`}>{ticket.priority}</span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">{ticket.status}</span>
                  </td>
                  <td className="p-4 text-xs text-slate-500">{new Date(ticket.updated_at).toLocaleString()}</td>
                  <td className="p-4">
                    <Button size="sm" variant="outline" onClick={() => openTicket(ticket)} data-testid={`view-ticket-${ticket.id}`}>View</Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{selected?.subject}</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3">
            {messages.map(msg => (
              <div key={msg.id} className={`p-3 rounded-lg ${msg.is_admin ? 'bg-blue-50 mr-8' : 'bg-slate-50 ml-8'}`}>
                <div className="text-xs text-slate-500 mb-1">{msg.is_admin ? 'Support' : 'You'} - {new Date(msg.created_at).toLocaleString()}</div>
                <div>{msg.message}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your message..." data-testid="ticket-reply-input" />
            <Button onClick={sendReply} data-testid="ticket-send-reply">Send</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Support;
